/**
 * HtmlViewer Component
 *
 * Displays HTML files with:
 * - Iframe preview (always)
 * - "Open in Browser" button to preview in new tab
 * - "Copy for Email" button (for files in an Emails folder)
 */

import { useState, useMemo } from 'react';
import { ExternalLink, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/services/api';
import { cn } from '@/lib/utils';
import { copyEmailToClipboard } from '@/services/emailUtils';

interface HtmlViewerProps {
  /** The HTML content to display */
  content: string;
  /** File path (used for preview URL) */
  path: string;
  /** File name */
  filename: string;
  /** Additional CSS classes */
  className?: string;
}

export function HtmlViewer({
  content,
  path,
  filename,
  className,
}: HtmlViewerProps) {
  const [copied, setCopied] = useState(false);

  // Check if this is an email file in any */Emails/* path.
  const isEmailFile = useMemo(() => {
    return path.includes('/Emails/') || path.includes('\\Emails\\');
  }, [path]);

  // Get the preview URL for opening in browser
  const previewUrl = useMemo(() => {
    return api.files.getDownloadUrl(path, true);
  }, [path]);

  // Handle copy for email
  const handleCopyForEmail = async () => {
    try {
      await copyEmailToClipboard(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  const handleOpenInBrowser = () => {
    window.open(previewUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={cn('h-full flex flex-col', className)}>
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-2 bg-surface-100 dark:bg-surface-800 border-b border-surface-200 dark:border-surface-700">
        <span className="text-sm font-medium text-surface-600 dark:text-surface-400 flex-1">
          {filename}
        </span>

        {/* Copy for Email button - only for files in an Emails folder */}
        {isEmailFile && (
          <Button
            variant="default"
            size="sm"
            onClick={handleCopyForEmail}
            className="gap-1.5"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                <span>Copy for Email</span>
              </>
            )}
          </Button>
        )}

        {/* Open in browser button */}
        <Button
          variant={isEmailFile ? "ghost" : "default"}
          size="sm"
          onClick={handleOpenInBrowser}
          className="gap-1.5"
        >
          <ExternalLink className="h-4 w-4" />
          <span>Open in Browser</span>
        </Button>
      </div>

      {/* Preview */}
      <div className="flex-1 overflow-hidden">
        <iframe
          src={previewUrl}
          title={`Preview: ${filename}`}
          className="w-full h-full border-0 bg-white"
        />
      </div>
    </div>
  );
}

export default HtmlViewer;
